import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Dashboard.module.css";
import { CircularProgress, TextField, Typography } from "@material-ui/core";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { fetchDashboardSummary } from "../../api";
import Empty from "../svgIcons/Empty";
import { Order } from "./Icons";
import Spinner from "../Spinner/Spinner";

const Dashboard = () => {
  const history = useHistory();
  const user = useMemo(() => JSON.parse(localStorage.getItem("profile")), []);

  // ---- Legacy "Past N Days" state (superseded by the global date-range filter below) ----
  // const [dueDays, setDueDays] = useState(5);
  // -----------------------------------------------------------------------------------------

  // Global Date Range filter — defaults to today for both start and end.
  const [startDate, setStartDate] = useState(moment().startOf("day").toDate());
  const [endDate, setEndDate] = useState(moment().startOf("day").toDate());

  const [summary, setSummary] = useState({
    startDate: null,
    endDate: null,
    totalBookingsCount: 0,
    inactiveClients: [],
    inactiveClientsCount: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const hasLoadedOnce = useRef(false);

  const loadDashboard = useCallback(async (rangeStart, rangeEnd, showRefresh = false) => {
    try {
      setError("");
      showRefresh ? setRefreshing(true) : setLoading(true);
      const { data } = await fetchDashboardSummary(
        moment(rangeStart).format("YYYY-MM-DD"),
        moment(rangeEnd).format("YYYY-MM-DD")
      );
      setSummary(data);
    } catch (err) {
      console.error("Dashboard summary failed", err);
      setError("Unable to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }

    // Reload whenever the global date range changes; use the refresh
    // (non full-page-spinner) treatment after the very first load.
    loadDashboard(startDate, endDate, hasLoadedOnce.current);
    hasLoadedOnce.current = true;
  }, [history, loadDashboard, user, startDate, endDate]);

  const filteredClients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return summary.inactiveClients || [];

    return (summary.inactiveClients || []).filter((client) =>
      `${client.name || ""} ${client.agentCode || ""}`
        .toLowerCase()
        .includes(query)
    );
  }, [searchQuery, summary.inactiveClients]);

  // ---- Legacy "Past N Days" handlers (superseded by the global date-range filter below) ----
  // const handleDueDaysChange = (event) => {
  //   const nextValue = Math.max(0, Number(event.target.value || 0));
  //   setDueDays(nextValue);
  // };
  //
  // const handleSubmit = () => {
  //   loadDashboard(dueDays, true);
  // };
  // --------------------------------------------------------------------------------------------

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const renderLastInvoice = (date) => {
    if (!date) return "Never";
    return moment(date).format("MMM DD, YYYY");
  };

  const formattedRange = `${moment(startDate).format("Do MMM YYYY")} - ${moment(
    endDate
  ).format("Do MMM YYYY")}`;

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <div>
          <Typography className={styles.eyebrow}>Dashboard</Typography>
          <h1 className={styles.title}>Booking Overview</h1>
        </div>

        <div className={styles.dateRangeFilter}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              label="Start Date"
              name="dashboardStartDate"
              value={startDate}
              onChange={handleStartDateChange}
              maxDate={endDate}
              format="dd/MM/yyyy"
              disabled={refreshing}
            />
            <KeyboardDatePicker
              label="End Date"
              name="dashboardEndDate"
              value={endDate}
              onChange={handleEndDateChange}
              minDate={startDate}
              format="dd/MM/yyyy"
              disabled={refreshing}
            />
          </MuiPickersUtilsProvider>
          {refreshing && <CircularProgress size={20} />}
        </div>
      </div>

      <section className={styles.dashboardGrid}>
        <Link
          to={{
            pathname: "/todayBookings",
            state: {
              fromDashboard: true,
              startDate: moment(startDate).format("YYYY-MM-DD"),
              endDate: moment(endDate).format("YYYY-MM-DD"),
            },
          }}
          className={styles.bookingCard}
        >
          <div className={styles.bookingContent}>
            <span className={styles.cardLabel}>Total Bookings</span>
            <strong>{summary.totalBookingsCount}</strong>
            <span className={styles.cardMeta}>View bookings for {formattedRange}</span>
          </div>
          <span className={styles.cardIcon}>
            <Order />
          </span>
        </Link>

        <section className={styles.inactivePanel}>
          <div className={styles.panelHeader}>
            <div>
              <Typography className={styles.panelEyebrow}>Agent Activity</Typography>
              <h2>Zero Booking Follow-up</h2>
            </div>

            {/* ---- Legacy "Past N Days" filter UI (superseded by the global date-range filter above) ----
            <div className={styles.filterControl}>
              <span>Past</span>
              <input
                id="dueDays"
                type="number"
                min="0"
                value={dueDays}
                onChange={handleDueDaysChange}
              />
              <span>days</span>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                disabled={refreshing}
              >
                {refreshing ? <CircularProgress size={18} /> : "Go"}
              </Button>
            </div>
            ---------------------------------------------------------------------------------------- */}
          </div>

          <div className={styles.infoBanner}>
            <strong>
              Found {summary.inactiveClientsCount} inactive agents with 0 bookings
              between {formattedRange}
            </strong>
            <span>
              Includes agents with no bookings in the selected date range and new agents
              with no bookings at all.
            </span>
          </div>

          <TextField
            className={styles.searchInput}
            fullWidth
            variant="outlined"
            placeholder="Search by agent name..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />

          <div className={styles.tableShell}>
            {error ? (
              <div className={styles.emptyState}>{error}</div>
            ) : filteredClients.length === 0 ? (
              <div className={styles.emptyState}>
                <Empty />
                <p>No inactive agents found for this search.</p>
              </div>
            ) : (
              <table className={styles.clientTable}>
                <thead>
                  <tr>
                    <th>Agent Name</th>
                    <th>Last Invoice</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((agent) => (
                    <tr key={agent.id}>
                      <td data-label="Agent Name">{agent.name}</td>
                      <td data-label="Last Invoice">
                        {renderLastInvoice(agent.lastInvoiceDate)}
                      </td>
                      <td data-label="Status">
                        <span
                          className={
                            agent.status === "New Client"
                              ? styles.newClientBadge
                              : styles.inactiveBadge
                          }
                        >
                          {agent.status === "New Client" ? "New Agent" : agent.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </section>
    </div>
  );
};

export default Dashboard;
